import { Box, Popover, Stack } from "@mui/material";
import { Button, Typography } from "@platform/design-system";
import { CustomCellEditorProps } from "ag-grid-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { HeapedCommissionValue } from "../../../../api/commissionHelpers";

import { HeapedAmountFields, parseClampedPercent } from "./HeapedAmountFields";

export interface HeapedCommissionEditorRef {
  getValue: () => HeapedCommissionValue | null;
  isPopup?: () => boolean;
}

/**
 * Popover-based editor for heaped commission with two fixed percentage fields
 * Always stores exactly two values: first year and thereafter
 */
export const HeapedCommissionEditor = forwardRef<
  HeapedCommissionEditorRef,
  CustomCellEditorProps
>((props, ref) => {
  const { stopEditing, api, node, column } = props;
  const onValueChange = (
    props as unknown as {
      onValueChange?: (value: HeapedCommissionValue | null) => void;
    }
  ).onValueChange;
  // Parse the value - it might be a string that needs parsing
  // Values can be null/undefined - no percentages required
  let initialValue: HeapedCommissionValue | null = null;
  if (typeof props.value === "string") {
    try {
      initialValue = JSON.parse(props.value) as HeapedCommissionValue;
    } catch {
      initialValue = null;
    }
  } else if (props.value && typeof props.value === "object") {
    initialValue = props.value as HeapedCommissionValue;
  }

  const [first, setFirst] = useState<string>(
    initialValue?.first?.toString() ?? "",
  );
  const [thereafter, setThereafter] = useState<string>(
    initialValue?.thereafter?.toString() ?? "",
  );

  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    getValue: () => {
      const firstNum = parseClampedPercent(first);
      const thereafterNum = parseClampedPercent(thereafter);

      if (firstNum === null && thereafterNum === null) return null;

      return {
        first: firstNum ?? 0,
        thereafter: thereafterNum ?? 0,
      };
    },
    isPopup: () => true,
  }));

  useEffect(() => {
    setOpen(true);
  }, []);

  const handleSave = (): void => {
    const firstNum = parseClampedPercent(first);
    const thereafterNum = parseClampedPercent(thereafter);

    const finalValue: HeapedCommissionValue | null =
      firstNum === null && thereafterNum === null
        ? null
        : { first: firstNum ?? 0, thereafter: thereafterNum ?? 0 };

    // Prefer AG Grid's editor callback when available; this is the native
    // commit path for React custom editors.
    if (onValueChange) {
      onValueChange(finalValue);
    } else if (node && column) {
      // Fallback for older editor integration paths.
      const columnField = column.getColId();
      node.setDataValue(columnField, finalValue);
    }

    setOpen(false);
    if (api) {
      api.stopEditing();
    } else {
      stopEditing();
    }
  };

  const handleEditorMouseDown = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    event.stopPropagation();
  };

  const handleCancel = (): void => {
    setOpen(false);
    stopEditing(true);
    api?.stopEditing(true);
  };

  return (
    <Box ref={anchorRef}>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleCancel}
        onMouseDown={handleEditorMouseDown}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          paper: {
            className: "ag-custom-component-popup",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="header-small" gutterBottom>
            Heaped Commission
          </Typography>

          <HeapedAmountFields
            first={first}
            thereafter={thereafter}
            onFirstChange={setFirst}
            onThereafterChange={setThereafter}
          />

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button onClick={handleCancel} variant="secondary">
              Cancel
            </Button>
            <Button onClick={handleSave} variant="primary">
              Save
            </Button>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
});

HeapedCommissionEditor.displayName = "HeapedCommissionEditor";
